import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type KeyboardEvent,
  type PointerEvent,
} from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  Building2,
  Loader2,
  MapPin,
  MessageSquare,
  Pencil,
  Phone,
  Search,
  SlidersHorizontal,
  Sparkles,
  Tags,
  TriangleAlert,
  UserRound,
  type LucideIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/atoms/button';
import { Checkbox } from '@/components/atoms/checkbox';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { Skeleton } from '@/components/atoms/skeleton';
import { Textarea } from '@/components/atoms/textarea';
import { FormPageLayout } from '@/components/layouts';
import {
  AddItemButton,
  FormSectionCard,
  FormSectionHeader,
  PhoneInput,
} from '@/components/molecules';
import {
  IrisMark,
  irisFieldClass,
  type IrisFieldMark,
} from '@/components/molecules/IrisFilledMark';
import { ContactAddressForm } from '@/components/organisms/contacts/address';
import {
  EMPTY_ADDRESS_DRAFT,
  draftSpecExtras,
  draftToEmbeddedAddress,
  isAddressDraftLocationComplete,
  isAddressDraftStarted,
  type AddressDraft,
} from '@/components/organisms/contacts/address';
import { CustomFieldsDraftSection } from '@/components/organisms/custom-fields/CustomFieldsDraftSection';
import {
  buildCustomFieldValues,
  customFieldDraftToPayloadValue,
  deriveCustomFieldDraftState,
} from '@/components/organisms/custom-fields/custom-field-form-utils';
import { TagSelector } from '@/components/organisms/TagSelector';
import { useContact } from '@/hooks/contacts/useContact';
import { normalizeContactAddress } from '@/hooks/contacts/useContacts';
import { useContactSearch } from '@/hooks/contacts/useContactSearch';
import { useCreateContact } from '@/hooks/contacts/useCreateContact';
import { useUpdateContact } from '@/hooks/contacts/useUpdateContact';
import { useCustomFieldSchema } from '@/hooks/custom-fields/useCustomFieldSchema';
import { useCustomFieldValues } from '@/hooks/custom-fields/useCustomFieldValues';
import { useUpsertValues } from '@/hooks/custom-fields/useUpsertValues';
import {
  buildContactFormPhonePayload,
  getContactAlternatePhone,
  getContactPrimaryPhone,
} from '@/lib/contacts/contactPhonePayload';
import { getContactTagIds } from '@/lib/contacts/contactTags';
import { isManualDisplayName } from '@/lib/contacts/displayName';
import { formatPhoneDisplay, normalizeContactPhoneToE164 } from '@/lib/contacts/phone';
import { showContactValidationWarningToasts } from '@/lib/contacts/validationWarnings';
import {
  LANGUAGE_OPTIONS,
  PREFERRED_TIME_OPTIONS,
  normalizePreferredTime,
} from '@/lib/contacts/preferenceOptions';
import { validateCustomFieldFromSchema } from '@/lib/custom-fields/validate-value';
import { isValidEmail } from '@/lib/validation';
import { apiGet, apiPatch, operationsClient } from '@/lib/api';
import { isApiError } from '@/lib/api/errors';
import type {
  CommunicationPreferences,
  Contact,
  ContactSource,
  ContactType,
  CreateContactDto,
  PreferredChannel,
  UpdateContactDto,
} from '@/types/contact';

const FORM_ID = 'contact-form';

const CONTACT_TYPES: { value: ContactType; label: string }[] = [
  { value: 'RESIDENTIAL', label: 'Residential' },
  { value: 'COMMERCIAL', label: 'Commercial' },
  // PROPERTY_MANAGER is intentionally hidden: it currently renders identically
  // to Commercial with no distinct fields/logic. The enum value is kept on the
  // backend so existing Property-Manager contacts still load and display.
];

const SOURCES: { value: ContactSource; label: string }[] = [
  { value: 'MANUAL', label: 'Manual' },
  { value: 'LEAD', label: 'Lead' },
  { value: 'REFERRAL', label: 'Referral' },
  { value: 'WEBSITE', label: 'Website' },
  { value: 'IMPORT', label: 'Import' },
];

const CHANNELS: { value: PreferredChannel; label: string }[] = [
  { value: 'PHONE', label: 'Phone' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'SMS', label: 'SMS' },
  { value: 'ALL', label: 'All' },
];

interface ContactFormState {
  firstName: string;
  lastName: string;
  title: string;
  displayName: string;
  email: string;
  phone: string;
  altPhone: string;
  contactType: ContactType;
  companyName: string;
  webAddress: string;
  source: ContactSource;
  parentContactId: string | null;
  preferredChannel: PreferredChannel;
  preferredTime: string;
  language: string;
  doNotCall: boolean;
  doNotEmail: boolean;
  notes: string;
  addresses: AddressDraft[];
  customFields: Record<string, string>;
}

export interface ContactCreatePrefill {
  name?: string | null;
  phone?: string | null;
  email?: string | null;
  address?: {
    street?: string;
    street2?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
  } | null;
}

export type ContactFormIrisMarks = Partial<
  Record<
    'customerName' | 'customerPhone' | 'email' | 'street' | 'street2' | 'city' | 'state' | 'zip',
    IrisFieldMark
  >
>;

interface ContactFormPanelProps {
  mode: 'create' | 'edit';
  contact?: Contact | null;
  isLoading?: boolean;
  onCancel: () => void;
  onSaved?: (contact: Contact) => void;
  /** Create flow: open an existing contact found by the finder / dedupe banner. */
  onOpenExisting?: (contactId: string) => void;
  /** Create flow: preselect the company/account this contact belongs to. */
  initialParentContactId?: string | null;
  /** Read once on mount. Remount the panel to begin a new prefilled draft. */
  initialValues?: ContactCreatePrefill;
  allowAddAnother?: boolean;
  stackHeaderOnMobile?: boolean;
  /** Explicit live-call updates, applied only to fields the operator has not edited. */
  autofillValues?: ContactCreatePrefill;
  irisMarks?: ContactFormIrisMarks;
}

/** Segmented contact-type control for the form header. */
function TypeSegmentedToggle({
  value,
  onChange,
  options,
}: {
  value: ContactType;
  onChange: (value: ContactType) => void;
  options: { value: ContactType; label: string }[];
}) {
  return (
    <div className="inline-flex max-w-full overflow-x-auto rounded-lg bg-muted p-0.5">
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={
              active
                ? 'whitespace-nowrap rounded-md bg-background px-3 py-1 text-sm font-medium text-foreground shadow-sm'
                : 'whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground'
            }
            aria-pressed={active}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function AddChip({
  active,
  icon,
  label,
  onClick,
}: {
  active: boolean;
  icon: ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <Button
      type="button"
      size="sm"
      variant={active ? 'default' : 'outline'}
      className="h-8 rounded-full px-3 text-xs"
      aria-pressed={active}
      onClick={onClick}
    >
      {icon}
      {label}
    </Button>
  );
}

function ContactFormSectionCard({
  icon,
  title,
  description,
  bodyClassName,
  children,
}: {
  icon: LucideIcon;
  title: string;
  description?: ReactNode;
  bodyClassName?: string;
  children: ReactNode;
}) {
  return (
    <FormSectionCard className="rounded-lg border bg-card p-3 shadow-none sm:p-4">
      <FormSectionHeader icon={icon} title={title} description={description} className="mb-3" />
      <div className={bodyClassName}>{children}</div>
    </FormSectionCard>
  );
}

function getContactName(contact?: Contact | null) {
  if (!contact) return '';
  return (
    contact.displayName ||
    [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim() ||
    contact.companyName ||
    ''
  );
}

function getPreference(contact: Contact | null | undefined, key: keyof CommunicationPreferences) {
  return contact?.communicationPreferences?.[key];
}

function getInitialForm(
  contact?: Contact | null,
  initialParentContactId?: string | null,
  initialValues?: ContactCreatePrefill,
): ContactFormState {
  const preferredChannel = (contact?.preferredContactMethod ??
    getPreference(contact, 'preferredChannel') ??
    'PHONE') as PreferredChannel;
  const communicationOptOut = new Set(contact?.communicationOptOut ?? []);
  const [firstName = '', ...lastName] = (initialValues?.name?.trim() ?? '').split(/\s+/);
  const address = initialValues?.address;

  return {
    firstName: contact?.firstName ?? firstName,
    lastName: contact?.lastName ?? lastName.join(' '),
    title: contact?.title ?? '',
    // Only keep a deliberately-set display name; auto/placeholder names start
    // empty so the live preview re-derives from first+last (or company).
    displayName: isManualDisplayName(contact) ? (contact?.displayName ?? '') : '',
    email: contact?.primaryEmail ?? initialValues?.email ?? '',
    phone: getContactPrimaryPhone(contact) ?? initialValues?.phone ?? '',
    altPhone: getContactAlternatePhone(contact) ?? '',
    contactType: contact?.contactType ?? 'RESIDENTIAL',
    companyName: contact?.companyName ?? '',
    webAddress: contact?.webAddress ?? '',
    source: (SOURCES.some((s) => s.value === contact?.source)
      ? contact!.source
      : 'MANUAL') as ContactSource,
    parentContactId: contact?.parentContactId ?? initialParentContactId ?? null,
    preferredChannel,
    preferredTime: String(getPreference(contact, 'preferredTime') ?? ''),
    language: contact?.language ?? String(getPreference(contact, 'language') ?? 'en'),
    doNotCall: Boolean(getPreference(contact, 'doNotCall') ?? communicationOptOut.has('PHONE')),
    doNotEmail: Boolean(getPreference(contact, 'doNotEmail') ?? communicationOptOut.has('EMAIL')),
    notes: contact?.notes ?? '',
    addresses: [
      {
        ...EMPTY_ADDRESS_DRAFT,
        isBilling: true,
        street1: address?.street ?? '',
        street2: address?.street2 ?? '',
        city: address?.city ?? '',
        state: address?.state ?? '',
        postalCode: address?.zip ?? '',
        country: address?.country ?? EMPTY_ADDRESS_DRAFT.country,
      },
    ],
    customFields: {},
  };
}

function validateAddresses(addresses: AddressDraft[]) {
  for (const [index, address] of addresses.entries()) {
    if (!isAddressDraftStarted(address)) continue;
    if (!isAddressDraftLocationComplete(address)) {
      toast.error(`Address ${index + 1} needs street, city, state, and ZIP.`);
      return false;
    }
    if (address.isPrimary && !address.isService) {
      toast.error(`Address ${index + 1} must be a service address before it can be primary.`);
      return false;
    }
  }
  return true;
}

function formatSummaryName(form: ContactFormState) {
  const personName = [form.firstName, form.lastName].filter(Boolean).join(' ').trim();
  return (
    form.displayName.trim() ||
    (form.contactType === 'RESIDENTIAL' ? personName : form.companyName.trim() || personName) ||
    'Contact'
  );
}

function hasCommunicationPreferences(form: ContactFormState) {
  return (
    form.preferredChannel !== 'PHONE' ||
    Boolean(form.preferredTime.trim()) ||
    form.language.trim() !== 'en' ||
    form.doNotCall ||
    form.doNotEmail
  );
}

function getInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/** Best-effort error message extraction for the partial-success warning toast. */
function extractCustomFieldErrorMessage(error: unknown): string {
  if (isApiError(error)) return error.message;
  if (error instanceof Error) return error.message;
  return 'unknown error';
}

function useDebouncedValue<T>(value: T, delayMs: number) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [delayMs, value]);

  return debounced;
}

export function ContactFormPanel({
  mode,
  contact,
  isLoading,
  onCancel,
  onSaved,
  onOpenExisting,
  initialParentContactId,
  initialValues,
  allowAddAnother = true,
  stackHeaderOnMobile = false,
  autofillValues,
  irisMarks,
}: ContactFormPanelProps) {
  const navigate = useNavigate();
  const createContact = useCreateContact();
  const updateContact = useUpdateContact();
  // The form orchestrates its own toasts (success → handled below, partial CF
  // failure → consolidated warning toast). Silence the upsert hook's auto
  // toasts to avoid double-firing.
  const upsertValues = useUpsertValues({ silent: true });
  const { data: contactSchema } = useCustomFieldSchema('contact');
  const { data: addressSchema } = useCustomFieldSchema('address');
  const { data: existingCustomFieldValues, isLoading: customFieldValuesLoading } =
    useCustomFieldValues('contact', mode === 'edit' ? (contact?.id ?? '') : '');

  // Parents may construct this object inline. Its identity must never reset
  // edits while this draft is open; a new dialog mount gets a new snapshot.
  const [createPrefill] = useState(initialValues);
  const [form, setForm] = useState<ContactFormState>(() =>
    getInitialForm(contact, initialParentContactId, createPrefill),
  );
  const [editedAutofillFields, setEditedAutofillFields] = useState(() => new Set<string>());
  function claimAutofillField(field: string) {
    setEditedAutofillFields((current) =>
      current.has(field) ? current : new Set([...current, field]),
    );
  }
  function noteAutofillClaim(
    event: KeyboardEvent<HTMLFormElement> | PointerEvent<HTMLFormElement>,
  ) {
    if ('key' in event) {
      const claims =
        event.ctrlKey || event.metaKey
          ? event.key.toLowerCase() === 'v'
          : !event.altKey &&
            (event.key.length === 1 || ['Enter', 'Backspace', 'Delete'].includes(event.key));
      if (!claims) return;
    }
    const target = event.target as HTMLElement;
    if (!target.closest('input, textarea, button, select')) return;
    const field = target.closest<HTMLElement>('[data-contact-autofill-field]')?.dataset
      .contactAutofillField;
    if (field) claimAutofillField(field);
  }
  const mark = (field: keyof ContactFormIrisMarks) =>
    editedAutofillFields.has(
      ['street', 'street2', 'city', 'state', 'zip'].includes(field) ? 'address' : field,
    )
      ? undefined
      : irisMarks?.[field];

  useEffect(() => {
    if (mode !== 'create' || !autofillValues) return;
    setForm((current) => {
      const next = { ...current };
      if (autofillValues.name != null && !editedAutofillFields.has('customerName')) {
        const [first = '', ...rest] = autofillValues.name.trim().split(/\s+/);
        next.firstName = first;
        next.lastName = rest.join(' ');
      }
      if (autofillValues.phone != null && !editedAutofillFields.has('customerPhone')) {
        next.phone = autofillValues.phone;
      }
      if (autofillValues.email != null && !editedAutofillFields.has('email')) {
        next.email = autofillValues.email;
      }
      const address = autofillValues.address;
      if (address && current.addresses[0] && !editedAutofillFields.has('address')) {
        const first = current.addresses[0];
        const updated = {
          ...first,
          street1: address.street ?? first.street1,
          street2: address.street2 ?? first.street2,
          city: address.city ?? first.city,
          state: address.state ?? first.state,
          postalCode: address.zip ?? first.postalCode,
          country: address.country ?? first.country,
        };
        if (
          Object.keys(updated).some(
            (key) => updated[key as keyof AddressDraft] !== first[key as keyof AddressDraft],
          )
        ) {
          next.addresses = [updated, ...current.addresses.slice(1)];
        }
      }
      return Object.keys(next).some(
        (key) => next[key as keyof ContactFormState] !== current[key as keyof ContactFormState],
      )
        ? next
        : current;
    });
  }, [autofillValues, mode, editedAutofillFields]);

  const [tagIds, setTagIds] = useState<string[]>([]);
  const [sections, setSections] = useState({
    // Address entry is a core part of contact creation and was too easy to miss
    // behind the optional-section chips. Keep it visible by default on create.
    addresses: mode === 'create',
    preferences: false,
    tagsNotes: false,
    customFields: false,
  });
  const [addressCfOpen, setAddressCfOpen] = useState<boolean[]>([]);
  const [activeCustomFieldKeys, setActiveCustomFieldKeys] = useState<string[]>([]);
  const [parentSearch, setParentSearch] = useState('');
  const [parentSearchOpen, setParentSearchOpen] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [altPhoneError, setAltPhoneError] = useState('');
  const [emailError, setEmailError] = useState('');
  // Inline custom-field validation errors, keyed by fieldKey for contact-level
  // fields and `${addressIndex}:${fieldKey}` for per-address fields. Mirrors
  // the BE rules client-side so we never round-trip a value the server will
  // reject and leave the user stranded on a "half-saved" form.
  const [customFieldErrors, setCustomFieldErrors] = useState<Record<string, string>>({});
  const [addressCustomFieldErrors, setAddressCustomFieldErrors] = useState<
    Record<number, Record<string, string>>
  >({});
  const [finderQuery, setFinderQuery] = useState('');
  const [finderOpen, setFinderOpen] = useState(false);
  const [dedupeDismissed, setDedupeDismissed] = useState(false);
  const [showAltPhone, setShowAltPhone] = useState(false);
  const [showDisplayName, setShowDisplayName] = useState(false);
  const customFieldsInitializedRef = useRef<string | null>(null);
  const addressCfInitRef = useRef(false);

  const isCreate = mode === 'create';
  const parentContactQuery = useContact(form.parentContactId ?? '');
  const trimmedParentSearch = parentSearch.trim();
  const debouncedParentSearch = useDebouncedValue(trimmedParentSearch, 300);
  const parentSearchReady =
    parentSearchOpen &&
    debouncedParentSearch.length >= 2 &&
    debouncedParentSearch === trimmedParentSearch;
  const parentSearchResults = useContactSearch(debouncedParentSearch, 8, {
    enabled: parentSearchOpen && debouncedParentSearch.length >= 2,
  });
  const finderResults = useContactSearch(finderQuery, 6, {
    enabled: isCreate && finderOpen && finderQuery.trim().length >= 2,
  });
  // Inline dedupe: a phone/email typed into Contact methods that matches an
  // existing contact surfaces a non-blocking warning (spec §852: override allowed).
  const dedupeQuery = form.phone.trim() || form.email.trim();
  const dedupeResults = useContactSearch(dedupeQuery, 3, {
    enabled: isCreate && !dedupeDismissed && dedupeQuery.length >= 3,
  });
  const dedupeMatch =
    isCreate && !dedupeDismissed
      ? (dedupeResults.data?.data ?? []).find((row) => row.id !== contact?.id)
      : undefined;

  const contactCustomFields = useMemo(
    () =>
      (Array.isArray(contactSchema) ? contactSchema : [])
        .filter((schema) => schema?.definition?.isActive && schema?.association?.isActive)
        .sort((left, right) => left.association.displayOrder - right.association.displayOrder),
    [contactSchema],
  );

  const addressCustomFields = useMemo(
    () =>
      (Array.isArray(addressSchema) ? addressSchema : [])
        .filter((schema) => schema?.definition?.isActive && schema?.association?.isActive)
        .sort((left, right) => left.association.displayOrder - right.association.displayOrder),
    [addressSchema],
  );

  const includeAddresses = mode === 'create';
  // R2 — there is no acquisition state here at all any more. The block is
  // stamped from the founding event and frozen by
  // trg_contact_acquisition_immutable, so this form neither offers it nor
  // tracks whether it has been set; the contact detail sidebar displays it.
  const isResidential = form.contactType === 'RESIDENTIAL';
  const identityComplete = isResidential
    ? Boolean(form.firstName.trim())
    : Boolean(form.companyName.trim());
  const canSave = identityComplete;
  const isSubmitting = createContact.isPending || updateContact.isPending || upsertValues.isPending;
  const hasTagsOrNotes = tagIds.length > 0 || Boolean(form.notes.trim());
  const hasCustomFields = activeCustomFieldKeys.length > 0;
  const hasAddressContent = includeAddresses && form.addresses.some(isAddressDraftStarted);
  const hasPreferenceContent = hasCommunicationPreferences(form);
  const summaryName = formatSummaryName(form);
  const optionalSectionChips = [
    {
      key: 'addresses' as const,
      label: 'Addresses',
      icon: <MapPin className="h-3.5 w-3.5" />,
      active: sections.addresses,
      available: includeAddresses,
    },
    {
      key: 'preferences' as const,
      label: 'Preferences',
      icon: <MessageSquare className="h-3.5 w-3.5" />,
      active: sections.preferences,
      available: true,
    },
    {
      key: 'tagsNotes' as const,
      label: 'Tags & notes',
      icon: <Tags className="h-3.5 w-3.5" />,
      active: sections.tagsNotes,
      available: true,
    },
    {
      key: 'customFields' as const,
      label: 'Custom fields',
      icon: <SlidersHorizontal className="h-3.5 w-3.5" />,
      active: sections.customFields,
      available: contactCustomFields.length > 0,
    },
  ].filter((chip) => chip.available);

  useEffect(() => {
    setForm(getInitialForm(contact, initialParentContactId, createPrefill));
    setTagIds(getContactTagIds(contact));
    setAddressCfOpen([]);
    addressCfInitRef.current = false;
    setParentSearch('');
    setParentSearchOpen(false);
    setPhoneError('');
    setFinderQuery('');
    setFinderOpen(false);
    setDedupeDismissed(false);
    setShowAltPhone(Boolean(getContactAlternatePhone(contact)));
    setShowDisplayName(Boolean(contact?.displayName));
    setCustomFieldErrors({});
    setAddressCustomFieldErrors({});
    customFieldsInitializedRef.current = null;
  }, [contact, initialParentContactId, createPrefill, mode]);

  useEffect(() => {
    if (contactCustomFields.length === 0) {
      setActiveCustomFieldKeys([]);
      return;
    }
    if (mode === 'edit' && customFieldValuesLoading) return;

    const initKey = `${mode}:${contact?.id ?? 'new'}:${contactCustomFields.length}:${
      existingCustomFieldValues?.length ?? 0
    }`;
    if (customFieldsInitializedRef.current === initKey) return;
    customFieldsInitializedRef.current = initKey;

    const next = deriveCustomFieldDraftState(
      mode === 'edit' ? existingCustomFieldValues : null,
      contactCustomFields,
      mode,
    );
    setForm((current) => ({ ...current, customFields: next.customFields }));
    setActiveCustomFieldKeys(next.activeFieldKeys);
    setSections((current) => ({ ...current, customFields: next.activeFieldKeys.length > 0 }));
  }, [contact?.id, contactCustomFields, customFieldValuesLoading, existingCustomFieldValues, mode]);

  useEffect(() => {
    setSections((current) => ({
      ...current,
      addresses: current.addresses || hasAddressContent,
      preferences: current.preferences || hasPreferenceContent,
      tagsNotes: current.tagsNotes || hasTagsOrNotes,
      customFields: current.customFields || hasCustomFields,
    }));
  }, [hasAddressContent, hasCustomFields, hasPreferenceContent, hasTagsOrNotes]);

  // Seed each draft address with the default address custom-field keys once the
  // address schema loads (create flow only — these are persisted to the address
  // custom-field endpoint after the contact+addresses are created).
  useEffect(() => {
    if (!includeAddresses) return;
    if (addressCfInitRef.current) return;
    if (addressCustomFields.length === 0) return;

    const next = deriveCustomFieldDraftState(null, addressCustomFields, 'create');
    setForm((current) => ({
      ...current,
      addresses: current.addresses.map((address) => ({
        ...address,
        customFields: next.customFields,
        activeCustomFieldKeys: next.activeFieldKeys,
      })),
    }));
    addressCfInitRef.current = true;
  }, [addressCustomFields, includeAddresses]);

  function set<K extends keyof ContactFormState>(key: K, value: ContactFormState[K]) {
    if (key === 'firstName' || key === 'lastName') claimAutofillField('customerName');
    if (key === 'phone') claimAutofillField('customerPhone');
    if (key === 'email') claimAutofillField('email');
    setForm((current) => ({ ...current, [key]: value }));
  }

  function toggleSection(key: keyof typeof sections) {
    setSections((current) => ({ ...current, [key]: !current[key] }));
  }

  function patchAddress(index: number, patch: Partial<AddressDraft>) {
    if (index === 0) claimAutofillField('address');
    setForm((current) => {
      const addresses = [...current.addresses];
      const target = addresses[index];
      if (!target) return current;
      addresses[index] = { ...target, ...patch };
      return { ...current, addresses };
    });
  }

  function toggleAddressBilling(index: number) {
    setForm((current) => {
      const addresses = current.addresses.map((address, currentIndex) =>
        currentIndex === index ? { ...address, isBilling: !address.isBilling } : address,
      );
      return { ...current, addresses };
    });
  }

  function setAddressCustomField(index: number, fieldKey: string, value: string) {
    setForm((current) => {
      const addresses = [...current.addresses];
      const target = addresses[index];
      if (!target) return current;
      addresses[index] = {
        ...target,
        customFields: { ...target.customFields, [fieldKey]: value },
      };
      return { ...current, addresses };
    });
  }

  function removeAddressCustomField(index: number, fieldKey: string) {
    patchAddress(index, {
      activeCustomFieldKeys: (form.addresses[index]?.activeCustomFieldKeys ?? []).filter(
        (key) => key !== fieldKey,
      ),
    });
  }

  function toggleAddressCfOpen(index: number, fallbackOpen: boolean) {
    setAddressCfOpen((current) => {
      const next = [...current];
      next[index] = !(next[index] ?? fallbackOpen);
      return next;
    });
  }

  // Service drives Primary: an address can't be primary unless it's a service address.
  function toggleAddressService(index: number) {
    setForm((current) => {
      const addresses = current.addresses.map((address, currentIndex) => {
        if (currentIndex !== index) return address;
        const isService = !address.isService;
        return { ...address, isService, isPrimary: isService ? address.isPrimary : false };
      });
      return { ...current, addresses };
    });
  }

  // Only one primary across addresses; turning one on clears the others.
  function toggleAddressPrimary(index: number) {
    setForm((current) => {
      const target = current.addresses[index];
      if (!target || !target.isService) return current;
      const turningOn = !target.isPrimary;
      const addresses = current.addresses.map((address, currentIndex) => {
        if (currentIndex === index) return { ...address, isPrimary: turningOn };
        return turningOn ? { ...address, isPrimary: false } : address;
      });
      return { ...current, addresses };
    });
  }

  function addAddress() {
    const seeded =
      addressCustomFields.length > 0
        ? deriveCustomFieldDraftState(null, addressCustomFields, 'create')
        : { customFields: {}, activeFieldKeys: [] };
    setForm((current) => ({
      ...current,
      addresses: [
        ...current.addresses,
        {
          ...EMPTY_ADDRESS_DRAFT,
          isPrimary: false,
          customFields: seeded.customFields,
          activeCustomFieldKeys: seeded.activeFieldKeys,
        },
      ],
    }));
    setAddressCfOpen((current) => [...current, seeded.activeFieldKeys.length > 0]);
  }

  function removeAddress(index: number) {
    setForm((current) => ({
      ...current,
      addresses: current.addresses.filter((_, currentIndex) => currentIndex !== index),
    }));
    setAddressCfOpen((current) => current.filter((_, currentIndex) => currentIndex !== index));
  }

  /**
   * Runs every active custom-field draft value through the client-side
   * validator (which mirrors the BE rules in `CustomFieldValidationService`).
   * Populates the inline error maps and returns true when everything passes.
   *
   * Originating bug: a NUMBER custom field with `max: 10` accepted "9999" on
   * the client, the BE accepted the `POST /contacts` half of the save, then
   * rejected the follow-up `POST /custom-field-values` — leaving the user
   * stranded on a form that looked broken even though the contact actually
   * persisted. Validating here short-circuits the half-success.
   */
  function validateCustomFieldDrafts() {
    const contactErrors: Record<string, string> = {};
    for (const fieldKey of activeCustomFieldKeys) {
      const schema = contactCustomFields.find((item) => item.definition.fieldKey === fieldKey);
      if (!schema) continue;
      const draftValue = form.customFields[fieldKey];
      if (draftValue === undefined || draftValue === '') continue;
      const payloadValue = customFieldDraftToPayloadValue(schema, draftValue);
      const result = validateCustomFieldFromSchema(schema, payloadValue);
      if (!result.ok) {
        contactErrors[fieldKey] = result.error;
      }
    }

    const addressErrors: Record<number, Record<string, string>> = {};
    if (includeAddresses) {
      form.addresses.forEach((address, index) => {
        if (!isAddressDraftStarted(address)) return;
        const perAddress: Record<string, string> = {};
        for (const fieldKey of address.activeCustomFieldKeys ?? []) {
          const schema = addressCustomFields.find((item) => item.definition.fieldKey === fieldKey);
          if (!schema) continue;
          const draftValue = address.customFields[fieldKey];
          if (draftValue === undefined || draftValue === '') continue;
          const payloadValue = customFieldDraftToPayloadValue(schema, draftValue);
          const result = validateCustomFieldFromSchema(schema, payloadValue);
          if (!result.ok) {
            perAddress[fieldKey] = result.error;
          }
        }
        if (Object.keys(perAddress).length > 0) {
          addressErrors[index] = perAddress;
        }
      });
    }

    setCustomFieldErrors(contactErrors);
    setAddressCustomFieldErrors(addressErrors);

    const hasContactErrors = Object.keys(contactErrors).length > 0;
    const hasAddressErrors = Object.keys(addressErrors).length > 0;
    if (hasContactErrors || hasAddressErrors) {
      if (hasAddressErrors) {
        // Auto-open every address that has an error so the inline message
        // isn't hidden behind a collapsed custom-fields slot.
        setAddressCfOpen((current) => {
          const next = [...current];
          for (const indexKey of Object.keys(addressErrors)) {
            next[Number(indexKey)] = true;
          }
          return next;
        });
      }
      if (hasContactErrors) {
        setSections((current) => ({ ...current, customFields: true }));
      }
      return false;
    }
    return true;
  }

  function validateForm() {
    if (!identityComplete) {
      toast.error(isResidential ? 'First name is required.' : 'Company is required.');
      return false;
    }

    if (form.phone.trim() && !normalizeContactPhoneToE164(form.phone)) {
      setPhoneError('Enter a valid phone number (e.g. +1 555 000-0000).');
      return false;
    }
    setPhoneError('');

    if (form.altPhone.trim() && !normalizeContactPhoneToE164(form.altPhone)) {
      setAltPhoneError('Enter a valid phone number (e.g. +1 555 000-0000).');
      return false;
    }
    setAltPhoneError('');

    if (form.email.trim() && !isValidEmail(form.email)) {
      setEmailError('Enter a valid email address (e.g. jane@example.com).');
      return false;
    }
    setEmailError('');

    if (includeAddresses && !validateAddresses(form.addresses)) return false;

    if (!validateCustomFieldDrafts()) {
      toast.error('Fix custom-field errors before saving.');
      return false;
    }

    return true;
  }

  function buildPayload() {
    const phonePayload = buildContactFormPhonePayload({
      contact,
      mode,
      primaryPhone: form.phone,
      alternatePhone: form.altPhone,
    });
    const email = form.email.trim();
    const displayName = form.displayName.trim();
    const firstName = form.firstName.trim();
    const lastName = form.lastName.trim();
    const title = form.title.trim();
    const companyName = form.companyName.trim();
    const webAddress = form.webAddress.trim();
    const communicationPreferences: CommunicationPreferences = {
      preferredChannel: form.preferredChannel,
      // 'any' (Anytime) = no explicit preference → store undefined.
      preferredTime:
        normalizePreferredTime(form.preferredTime) === 'any'
          ? undefined
          : normalizePreferredTime(form.preferredTime),
      language: form.language.trim() || 'en',
      doNotCall: form.doNotCall,
      doNotEmail: form.doNotEmail,
    };

    const basePayload: CreateContactDto = {
      firstName: firstName || undefined,
      lastName: lastName || undefined,
      title: !isResidential ? title || undefined : undefined,
      displayName: displayName || undefined,
      primaryEmail: email || undefined,
      contactType: form.contactType,
      companyName: companyName || undefined,
      webAddress: webAddress || undefined,
      parentContactId: form.parentContactId ?? undefined,
      preferredContactMethod: form.preferredChannel,
      language: form.language.trim() || undefined,
      communicationPreferences,
      communicationOptOut: [
        ...(form.doNotCall ? (['PHONE'] as PreferredChannel[]) : []),
        ...(form.doNotEmail ? (['EMAIL'] as PreferredChannel[]) : []),
      ],
      notes: form.notes.trim() || undefined,
      tagIds: tagIds.length > 0 ? tagIds : undefined,
    };

    if (includeAddresses) {
      const addresses = form.addresses.filter(isAddressDraftStarted).map(draftToEmbeddedAddress);
      if (addresses.length > 0) basePayload.addresses = addresses;
    }

    if (mode === 'edit') {
      return {
        id: contact?.id ?? '',
        ...basePayload,
        ...(phonePayload as Partial<UpdateContactDto>),
        firstName: firstName || undefined,
        lastName: lastName || undefined,
        title: !isResidential ? title || null : null,
        // null (not undefined) clears a stale stored display name so the backend
        // re-derives it from the updated first+last / company on save.
        displayName: displayName || null,
        companyName: companyName || null,
        webAddress: webAddress || null,
        primaryEmail: email || null,
        // Acquisition is never sent from this form (R2): it is stamped from the
        // founding event and frozen by trg_contact_acquisition_immutable, so
        // any value here would be an edit the API refuses. `source` is system
        // provenance with no control in this form ('inbound-sms',
        // 'quickbooks', …), so it is never sent on update either.
        notes: form.notes.trim() || null,
        tagIds,
        parentContactId: form.parentContactId,
      } satisfies UpdateContactDto;
    }

    return {
      ...basePayload,
      source: form.source,
      ...(phonePayload as Partial<CreateContactDto>),
    } satisfies CreateContactDto;
  }

  async function persistCustomFields(contactId: string) {
    const values = buildCustomFieldValues(
      form.customFields,
      contactCustomFields,
      [],
      activeCustomFieldKeys,
    );
    if (values.length === 0) return;
    await upsertValues.mutateAsync({
      entityType: 'contact',
      entityId: contactId,
      values,
    });
  }

  // The embedded create payload only carries the lightweight address fields and
  // its response does not echo the created addresses, so after the contact is
  // created we re-fetch its addresses to learn their ids, then PATCH the spec
  // extras (title/label/tax) and persist each address's custom-field values.
  async function persistAddressExtras(savedId: string) {
    if (!includeAddresses) return;
    const startedDrafts = form.addresses.filter(isAddressDraftStarted);
    if (startedDrafts.length === 0) return;

    const pending = startedDrafts.map((draft) => ({
      draft,
      extras: draftSpecExtras(draft),
      values: buildCustomFieldValues(
        draft.customFields,
        addressCustomFields,
        [],
        draft.activeCustomFieldKeys,
      ),
    }));
    if (pending.every((item) => !item.extras && item.values.length === 0)) return;

    let savedAddresses: ReturnType<typeof normalizeContactAddress>[] = [];
    try {
      const raw = await apiGet<Record<string, unknown>[]>(
        operationsClient,
        `/contacts/${savedId}/addresses`,
      );
      savedAddresses = raw.map(normalizeContactAddress);
    } catch {
      return; // Can't match without the created address ids.
    }

    for (let index = 0; index < pending.length; index += 1) {
      const { draft, extras, values } = pending[index]!;
      if (!extras && values.length === 0) continue;
      const match =
        savedAddresses.find(
          (addr) =>
            (addr.street1 ?? addr.street ?? '').trim() === draft.street1.trim() &&
            (addr.postalCode ?? addr.zip ?? '').trim() === draft.postalCode.trim(),
        ) ?? savedAddresses[index];
      const addressId = match?.id;
      if (!addressId) continue;

      try {
        if (extras) {
          await apiPatch(operationsClient, `/contacts/${savedId}/addresses/${addressId}`, extras);
        }
        if (values.length > 0) {
          await upsertValues.mutateAsync({
            entityType: 'address',
            entityId: addressId,
            values,
          });
        }
      } catch {
        // Best-effort: the contact is already saved; extras must not block it.
      }
    }
  }

  function resetForNewContact() {
    setForm(getInitialForm(null, initialParentContactId));
    setTagIds([]);
    setAddressCfOpen([]);
    addressCfInitRef.current = false;
    setActiveCustomFieldKeys([]);
    setFinderQuery('');
    setFinderOpen(false);
    setDedupeDismissed(false);
    setShowAltPhone(false);
    setShowDisplayName(false);
    setPhoneError('');
    setCustomFieldErrors({});
    setAddressCustomFieldErrors({});
    customFieldsInitializedRef.current = null;
  }

  async function submit(addAnother: boolean) {
    if (!validateForm()) return;

    // Stage 1: create / update the contact itself. If this fails, the mutation
    // hook already surfaced an error toast — bail.
    let saved;
    try {
      const payload = buildPayload();
      saved =
        mode === 'edit'
          ? await updateContact.mutateAsync(payload as UpdateContactDto)
          : await createContact.mutateAsync(payload as CreateContactDto);
    } catch {
      return;
    }

    // Stage 2: persist custom-field + address-extra values. These calls are
    // best-effort. A failure here used to swallow into the outer catch and
    // strand the user — onSaved was never called and the form looked broken
    // even though the contact actually existed in the DB. Now we capture the
    // failures, emit one consolidated warning toast, and still complete the
    // navigation so the user understands "contact saved, fix this field".
    const customFieldFailures: string[] = [];
    try {
      await persistCustomFields(saved.id);
    } catch (error) {
      customFieldFailures.push(extractCustomFieldErrorMessage(error));
    }
    try {
      await persistAddressExtras(saved.id);
    } catch (error) {
      customFieldFailures.push(extractCustomFieldErrorMessage(error));
    }

    if (customFieldFailures.length > 0) {
      // Sonner v1's toast.warning falls back to a neutral toast on themes that
      // don't define one, so spell out the message verbatim either way.
      toast.warning(
        `Contact saved, but some custom fields weren't: ${customFieldFailures.join('; ')}`,
      );
    }

    showContactValidationWarningToasts(saved.validationWarnings, {
      onViewContact: (contactId) => {
        if (onOpenExisting) {
          onOpenExisting(contactId);
          return;
        }

        void navigate({
          to: '/dashboard/contacts/$contactId',
          params: { contactId },
        });
      },
    });
    if (addAnother && mode === 'create') {
      toast.success('Contact created. Add another.');
      resetForNewContact();
    } else {
      onSaved?.(saved);
    }
  }

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    // A dialog is portalled out of its parent's DOM form, but React submit
    // events still bubble through that parent (e.g. Create Job).
    event.stopPropagation();
    void submit(false);
  }

  if (isLoading || (mode === 'edit' && !contact)) {
    return (
      <FormPageLayout
        title={mode === 'edit' ? 'Edit Contact' : 'New Contact'}
        onBack={onCancel}
        noscroll
        stackHeaderOnMobile={stackHeaderOnMobile}
        actions={
          <Button type="button" size="sm" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        }
      >
        <div className="space-y-4 px-4 py-4">
          <Skeleton className="h-24 w-full rounded-xl" />
          <Skeleton className="h-36 w-full rounded-xl" />
          <Skeleton className="h-32 w-full rounded-xl" />
        </div>
      </FormPageLayout>
    );
  }

  return (
    <FormPageLayout
      title={mode === 'edit' ? 'Edit Contact' : 'New Contact'}
      onBack={onCancel}
      stackHeaderOnMobile={stackHeaderOnMobile}
      titleExtra={
        <TypeSegmentedToggle
          value={form.contactType}
          onChange={(value) => set('contactType', value)}
          options={CONTACT_TYPES}
        />
      }
      actions={
        <>
          <Button type="button" variant="outline" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          {isCreate && allowAddAnother ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void submit(true)}
              disabled={isSubmitting || !canSave}
            >
              Save &amp; add another
            </Button>
          ) : null}
          <Button type="submit" size="sm" form={FORM_ID} disabled={isSubmitting || !canSave}>
            {isSubmitting && <Loader2 size={14} className="mr-1.5 animate-spin" />}
            {mode === 'edit' ? 'Save' : 'Create'}
          </Button>
        </>
      }
    >
      <div className="min-h-full bg-muted/40">
        <form
          id={FORM_ID}
          onSubmit={handleSubmit}
          onPointerDownCapture={noteAutofillClaim}
          onKeyDownCapture={noteAutofillClaim}
          noValidate
          className="mx-auto max-w-4xl space-y-3 px-4 py-5"
        >
          <div className="space-y-3">
            {isCreate ? (
              <div className="relative">
                <div className="flex items-center gap-2.5 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2.5 text-sm text-primary">
                  <Sparkles className="size-4 shrink-0" />
                  <input
                    value={finderQuery}
                    onChange={(event) => {
                      setFinderQuery(event.target.value);
                      setFinderOpen(true);
                    }}
                    onFocus={() => setFinderOpen(true)}
                    onBlur={() => window.setTimeout(() => setFinderOpen(false), 150)}
                    placeholder="Type a phone, email, or name — I'll find an existing contact"
                    className="min-w-0 flex-1 bg-transparent text-foreground placeholder:text-primary/50 focus:outline-none"
                  />
                  <Search className="size-3.5 shrink-0 text-primary/50" />
                </div>
                {finderOpen &&
                finderQuery.trim().length >= 2 &&
                (finderResults.data?.data?.length ?? 0) > 0 ? (
                  <div className="absolute left-0 right-0 top-full z-40 mt-1 max-h-64 overflow-y-auto rounded-md border bg-popover py-1 shadow-md">
                    {finderResults.data?.data?.map((row) => (
                      <button
                        key={row.id}
                        type="button"
                        className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-muted/60"
                        onMouseDown={(event) => {
                          event.preventDefault();
                          onOpenExisting?.(row.id);
                        }}
                      >
                        <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-semibold text-muted-foreground">
                          {getInitials(row.displayName)}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-medium">{row.displayName}</span>
                          {row.primaryPhone || row.primaryEmail ? (
                            <span className="block truncate text-xs text-muted-foreground">
                              {row.primaryPhone
                                ? formatPhoneDisplay(row.primaryPhone)
                                : row.primaryEmail}
                            </span>
                          ) : null}
                        </span>
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}

            <ContactFormSectionCard
              icon={UserRound}
              title="Identity"
              description="Name and display details for this contact."
            >
              <div className="space-y-4">
                {isResidential ? (
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="first-name">
                        First name <span className="text-destructive">*</span>
                        <IrisMark mark={mark('customerName')} />
                      </Label>
                      <Input
                        id="first-name"
                        data-contact-autofill-field="customerName"
                        className={irisFieldClass(mark('customerName'))}
                        value={form.firstName}
                        onChange={(event) => set('firstName', event.target.value)}
                        placeholder="Jane"
                        required
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="last-name">
                        Last name
                        <IrisMark mark={mark('customerName')} />
                      </Label>
                      <Input
                        id="last-name"
                        data-contact-autofill-field="customerName"
                        className={irisFieldClass(mark('customerName'))}
                        value={form.lastName}
                        onChange={(event) => set('lastName', event.target.value)}
                        placeholder="Doe"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <Label htmlFor="company-name">
                          Business name <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="company-name"
                          value={form.companyName}
                          onChange={(event) => set('companyName', event.target.value)}
                          placeholder="Acme Corp"
                          required
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="web-address">Website</Label>
                        <Input
                          id="web-address"
                          value={form.webAddress}
                          onChange={(event) => set('webAddress', event.target.value)}
                          placeholder="https://example.com"
                          autoComplete="url"
                        />
                      </div>
                    </div>
                    <div className="space-y-3 rounded-lg border border-dashed bg-muted/30 p-3">
                      <p className="text-xs font-medium text-muted-foreground">
                        Contact person <span className="font-normal">— optional</span>
                      </p>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="space-y-1.5">
                          <Label htmlFor="first-name">
                            First name
                            <IrisMark mark={mark('customerName')} />
                          </Label>
                          <Input
                            id="first-name"
                            data-contact-autofill-field="customerName"
                            className={irisFieldClass(mark('customerName'))}
                            value={form.firstName}
                            onChange={(event) => set('firstName', event.target.value)}
                            placeholder="Contact first name"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="last-name">
                            Last name
                            <IrisMark mark={mark('customerName')} />
                          </Label>
                          <Input
                            id="last-name"
                            data-contact-autofill-field="customerName"
                            className={irisFieldClass(mark('customerName'))}
                            value={form.lastName}
                            onChange={(event) => set('lastName', event.target.value)}
                            placeholder="Contact last name"
                          />
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="contact-title">
                          Role / title{' '}
                          <span className="font-normal text-muted-foreground">— optional</span>
                        </Label>
                        <Input
                          id="contact-title"
                          value={form.title}
                          onChange={(event) => set('title', event.target.value)}
                          placeholder="e.g. Accounts Payable"
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-1.5">
                  {showDisplayName ? (
                    <>
                      <Label htmlFor="display-name">Display name</Label>
                      <Input
                        id="display-name"
                        value={form.displayName}
                        onChange={(event) => set('displayName', event.target.value)}
                        placeholder={summaryName === 'Contact' ? 'Auto-generated' : summaryName}
                      />
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Display name:{' '}
                      <span className="font-medium text-foreground">{summaryName}</span> ·{' '}
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 font-medium text-primary underline underline-offset-2 hover:opacity-80"
                        onClick={() => setShowDisplayName(true)}
                      >
                        <Pencil className="size-3" />
                        Edit display name
                      </button>
                    </p>
                  )}
                </div>
              </div>
            </ContactFormSectionCard>

            <ContactFormSectionCard
              icon={Building2}
              title="Account relationship"
              description="Link this contact to the company or parent account it belongs to."
            >
              <div className="space-y-1.5">
                <Label htmlFor="parent-contact">
                  Parent company / account{' '}
                  <span className="font-normal text-muted-foreground">— optional</span>
                </Label>
                {form.parentContactId ? (
                  <div className="flex items-center justify-between gap-2 rounded-md border bg-muted/30 px-3 py-2 text-sm">
                    <span className="truncate">
                      {parentContactQuery.data
                        ? getContactName(parentContactQuery.data)
                        : form.parentContactId}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2"
                      onClick={() => {
                        set('parentContactId', null);
                        setParentSearch('');
                        setParentSearchOpen(false);
                      }}
                    >
                      Clear
                    </Button>
                  </div>
                ) : (
                  <div className="relative">
                    <Input
                      id="parent-contact"
                      value={parentSearch}
                      onFocus={() => setParentSearchOpen(true)}
                      onChange={(event) => {
                        setParentSearch(event.target.value);
                        setParentSearchOpen(true);
                      }}
                      placeholder="Search companies or contacts…"
                      autoComplete="off"
                    />
                    {parentSearchReady && (parentSearchResults.data?.data?.length ?? 0) > 0 ? (
                      <div className="absolute left-0 right-0 top-full z-40 mt-1 max-h-56 overflow-y-auto rounded-md border bg-popover py-1 shadow-md">
                        {parentSearchResults.data?.data
                          ?.filter((row) => row.id !== contact?.id)
                          .map((row) => (
                            <button
                              key={row.id}
                              type="button"
                              className="flex w-full flex-col items-start px-3 py-2 text-left text-sm hover:bg-muted/60"
                              onMouseDown={(event) => {
                                event.preventDefault();
                                set('parentContactId', row.id);
                                setParentSearch('');
                                setParentSearchOpen(false);
                              }}
                            >
                              <span className="font-medium">{row.displayName}</span>
                              <span className="text-xs text-muted-foreground">
                                {row.companyName || row.primaryEmail || row.contactType}
                              </span>
                            </button>
                          ))}
                      </div>
                    ) : null}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  Linked contacts remain separate customer records while appearing under the same
                  company account.
                </p>
              </div>
            </ContactFormSectionCard>

            <ContactFormSectionCard
              icon={Phone}
              title="Contact methods"
              description="Phone, email, preferred channel, and duplicate checks."
            >
              <div className="space-y-4">
                <p className="text-xs text-muted-foreground">
                  Phone and email are optional. Add them later to enable notifications.
                </p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="phone">
                      Phone
                      <IrisMark mark={mark('customerPhone')} />
                    </Label>
                    <PhoneInput
                      id="phone"
                      data-contact-autofill-field="customerPhone"
                      className={irisFieldClass(mark('customerPhone'))}
                      value={form.phone}
                      onChange={(value) => {
                        set('phone', value);
                        setPhoneError('');
                      }}
                      onBlur={() =>
                        setPhoneError(
                          form.phone.trim() && !normalizeContactPhoneToE164(form.phone)
                            ? 'Enter a valid phone number (e.g. +1 555 000-0000).'
                            : '',
                        )
                      }
                      placeholder="(555) 000-0000"
                      aria-invalid={!!phoneError}
                    />
                    {phoneError && <p className="text-xs text-destructive">{phoneError}</p>}
                    {showAltPhone ? null : (
                      <AddItemButton
                        label="Add alternate phone"
                        onClick={() => setShowAltPhone(true)}
                      />
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="email">
                      Email
                      <IrisMark mark={mark('email')} />
                    </Label>
                    <Input
                      id="email"
                      data-contact-autofill-field="email"
                      className={irisFieldClass(mark('email'))}
                      type="email"
                      value={form.email}
                      onChange={(event) => {
                        set('email', event.target.value);
                        setEmailError('');
                      }}
                      onBlur={() =>
                        setEmailError(
                          form.email.trim() && !isValidEmail(form.email)
                            ? 'Enter a valid email address (e.g. jane@example.com).'
                            : '',
                        )
                      }
                      aria-invalid={!!emailError}
                      placeholder="jane@example.com"
                    />
                    {emailError && <p className="text-xs text-destructive">{emailError}</p>}
                  </div>
                </div>

                {showAltPhone ? (
                  <div className="space-y-1.5 sm:max-w-[calc(50%-0.5rem)]">
                    <Label htmlFor="alt-phone">Alternate phone</Label>
                    <PhoneInput
                      id="alt-phone"
                      value={form.altPhone}
                      onChange={(value) => {
                        set('altPhone', value);
                        setAltPhoneError('');
                      }}
                      onBlur={() =>
                        setAltPhoneError(
                          form.altPhone.trim() && !normalizeContactPhoneToE164(form.altPhone)
                            ? 'Enter a valid phone number (e.g. +1 555 000-0000).'
                            : '',
                        )
                      }
                      aria-invalid={!!altPhoneError}
                      placeholder="(555) 000-0000"
                    />
                    {altPhoneError && <p className="text-xs text-destructive">{altPhoneError}</p>}
                  </div>
                ) : null}

                <div className="space-y-1.5 sm:max-w-[240px]">
                  <Label htmlFor="preferred-channel">Preferred contact</Label>
                  <Select
                    value={form.preferredChannel}
                    onValueChange={(value) => set('preferredChannel', value as PreferredChannel)}
                  >
                    <SelectTrigger id="preferred-channel">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CHANNELS.map((channel) => (
                        <SelectItem key={channel.value} value={channel.value}>
                          {channel.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {dedupeMatch ? (
                  <div className="flex items-start gap-2.5 rounded-md border border-warning/30 bg-warning/10 px-3 py-2.5 text-sm text-warning">
                    <TriangleAlert className="mt-0.5 size-4 shrink-0 text-warning" />
                    <span className="min-w-0 flex-1">
                      This {form.phone.trim() ? 'number' : 'email'} matches{' '}
                      <span className="font-semibold">{dedupeMatch.displayName}</span>
                      {dedupeMatch.companyName ? ` (${dedupeMatch.companyName})` : ''}. Use existing
                      instead?
                    </span>
                    <span className="flex shrink-0 gap-1.5">
                      <Button
                        type="button"
                        size="sm"
                        className="h-7 bg-warning text-warning-foreground hover:bg-warning/90"
                        onClick={() => onOpenExisting?.(dedupeMatch.id)}
                      >
                        Use existing
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-7 text-warning hover:bg-warning/10"
                        onClick={() => setDedupeDismissed(true)}
                      >
                        Dismiss
                      </Button>
                    </span>
                  </div>
                ) : null}
              </div>
            </ContactFormSectionCard>

            <div className="flex flex-wrap items-center gap-2 px-1">
              <span className="mr-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Add if needed
              </span>
              {optionalSectionChips.map((chip) => (
                <AddChip
                  key={chip.key}
                  active={chip.active}
                  icon={chip.icon}
                  label={chip.label}
                  onClick={() => toggleSection(chip.key)}
                />
              ))}
            </div>

            {includeAddresses && sections.addresses ? (
              <ContactFormSectionCard
                icon={MapPin}
                title="Addresses"
                description="Service, billing, and primary address details."
                bodyClassName="space-y-4"
              >
                {form.addresses.map((address, index) => {
                  const cfFallbackOpen = address.activeCustomFieldKeys.length > 0;
                  const customFieldsSlot =
                    addressCustomFields.length > 0 ? (
                      <CustomFieldsDraftSection
                        open={addressCfOpen[index] ?? cfFallbackOpen}
                        onToggle={() => toggleAddressCfOpen(index, cfFallbackOpen)}
                        schemas={addressCustomFields}
                        activeFieldKeys={address.activeCustomFieldKeys}
                        values={address.customFields}
                        fieldErrors={addressCustomFieldErrors[index]}
                        onFieldChange={(fieldKey, value) => {
                          setAddressCustomField(index, fieldKey, value);
                          if (addressCustomFieldErrors[index]?.[fieldKey]) {
                            setAddressCustomFieldErrors((current) => {
                              const perAddress = { ...(current[index] ?? {}) };
                              delete perAddress[fieldKey];
                              const next = { ...current };
                              if (Object.keys(perAddress).length > 0) {
                                next[index] = perAddress;
                              } else {
                                delete next[index];
                              }
                              return next;
                            });
                          }
                        }}
                        onActiveFieldKeysChange={(keys) =>
                          patchAddress(index, { activeCustomFieldKeys: keys })
                        }
                        onRemoveField={(fieldKey) => removeAddressCustomField(index, fieldKey)}
                      />
                    ) : undefined;

                  return (
                    <div
                      key={index}
                      data-contact-autofill-field={index === 0 ? 'address' : undefined}
                    >
                      <ContactAddressForm
                        irisMarks={
                          index === 0
                            ? {
                                street: mark('street'),
                                street2: mark('street2'),
                                city: mark('city'),
                                state: mark('state'),
                                zip: mark('zip'),
                              }
                            : undefined
                        }
                        value={address}
                        onChange={(patch) => patchAddress(index, patch)}
                        title={`Address ${index + 1}`}
                        onToggleService={() => toggleAddressService(index)}
                        onToggleBilling={() => toggleAddressBilling(index)}
                        onTogglePrimary={() => toggleAddressPrimary(index)}
                        canRemove={form.addresses.length > 1}
                        onRemove={() => removeAddress(index)}
                        customFieldsSlot={customFieldsSlot}
                      />
                    </div>
                  );
                })}

                <AddItemButton label="Add billing or job site address" onClick={addAddress} />
              </ContactFormSectionCard>
            ) : null}

            {sections.preferences ? (
              <ContactFormSectionCard
                icon={MessageSquare}
                title="Preferences"
                description="Language and contact rules."
                bodyClassName="space-y-3"
              >
                {/* No acquisition control here by design (R2). Acquisition is
                    stamped from the founding event and frozen by
                    trg_contact_acquisition_immutable; it is displayed read-only
                    on the contact detail sidebar. */}
                <div className="grid gap-3 sm:grid-cols-[1fr_8rem]">
                  <div className="space-y-1">
                    <Label htmlFor="preferred-time">Preferred time</Label>
                    <Select
                      value={normalizePreferredTime(form.preferredTime)}
                      onValueChange={(value) => set('preferredTime', value)}
                    >
                      <SelectTrigger id="preferred-time" className="w-full">
                        <SelectValue placeholder="Anytime" />
                      </SelectTrigger>
                      <SelectContent>
                        {PREFERRED_TIME_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="language">Language</Label>
                    <Select
                      value={form.language || 'en'}
                      onValueChange={(value) => set('language', value)}
                    >
                      <SelectTrigger id="language" className="w-full">
                        <SelectValue placeholder="English" />
                      </SelectTrigger>
                      <SelectContent>
                        {LANGUAGE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <label className="flex w-fit cursor-pointer items-center gap-2 text-sm">
                  <Checkbox
                    checked={form.doNotCall && form.doNotEmail}
                    onCheckedChange={(checked) => {
                      const next = Boolean(checked);
                      set('doNotCall', next);
                      set('doNotEmail', next);
                    }}
                  />
                  Do not contact
                </label>
              </ContactFormSectionCard>
            ) : null}

            {sections.tagsNotes ? (
              <ContactFormSectionCard
                icon={Tags}
                title="Tags & notes"
                description="Internal classification and contact notes."
                bodyClassName="space-y-4"
              >
                <div className="space-y-1.5">
                  <Label>Tags</Label>
                  <TagSelector scope="contact" value={tagIds} onChange={setTagIds} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    rows={4}
                    value={form.notes}
                    onChange={(event) => set('notes', event.target.value)}
                    placeholder="Additional notes"
                  />
                </div>
              </ContactFormSectionCard>
            ) : null}

            {contactCustomFields.length > 0 && sections.customFields ? (
              <CustomFieldsDraftSection
                schemas={contactCustomFields}
                activeFieldKeys={activeCustomFieldKeys}
                values={form.customFields}
                title="Custom fields"
                icon={SlidersHorizontal}
                fieldErrors={customFieldErrors}
                onFieldChange={(fieldKey, value) => {
                  setForm((current) => ({
                    ...current,
                    customFields: { ...current.customFields, [fieldKey]: value },
                  }));
                  // Clear the inline error as soon as the user edits — they'll
                  // re-validate on the next submit, no point shouting at them.
                  if (customFieldErrors[fieldKey]) {
                    setCustomFieldErrors((current) => {
                      const next = { ...current };
                      delete next[fieldKey];
                      return next;
                    });
                  }
                }}
                onActiveFieldKeysChange={setActiveCustomFieldKeys}
                onRemoveField={(fieldKey) => {
                  setActiveCustomFieldKeys((current) => current.filter((key) => key !== fieldKey));
                  setForm((current) => {
                    const customFields = { ...current.customFields };
                    delete customFields[fieldKey];
                    return { ...current, customFields };
                  });
                  if (customFieldErrors[fieldKey]) {
                    setCustomFieldErrors((current) => {
                      const next = { ...current };
                      delete next[fieldKey];
                      return next;
                    });
                  }
                }}
              />
            ) : null}
          </div>
        </form>
      </div>
    </FormPageLayout>
  );
}
